if (!customElements.get('collection-facet-nav-scroller')) {
  class CollectionFacetNavScroller extends HTMLElement {
    connectedCallback() {
      this.list = this.querySelector('.collection-facet-nav__pills');
      this.prev = this.querySelector('.collection-facet-nav__arrow--prev');
      this.next = this.querySelector('.collection-facet-nav__arrow--next');
      if (!this.list || !this.prev || !this.next) return;

      this.update = this.update.bind(this);

      this.prev.addEventListener('click', () => this.scrollByPage(-1));
      this.next.addEventListener('click', () => this.scrollByPage(1));
      // passive: this listener only reads scroll position, so it must never
      // block the scroll it is observing.
      this.list.addEventListener('scroll', this.update, { passive: true });

      // The row's own width changes with the viewport, and its content width
      // changes once lazy-loaded thumbnails settle — observing the element
      // covers both without a resize listener plus a load listener per image.
      if ('ResizeObserver' in window) {
        this.observer = new ResizeObserver(this.update);
        this.observer.observe(this.list);
      } else {
        window.addEventListener('resize', this.update);
      }

      this.update();
      this.revealCurrent();

      /* Thumbnails are lazy-loaded, so the row's width — and therefore where
         the current value sits in it — is not final at connect time. One
         more pass after load settles it without polling. */
      if (document.readyState !== 'complete') {
        window.addEventListener('load', () => this.revealCurrent(), { once: true });
      }
    }

    /* On a value page the current pill can sit anywhere in the row, and the
       row always starts at scrollLeft 0 — so on, say, "Living Room Tiles"
       the shopper landed on a row scrolled to the far left, with the one
       pill telling them where they are off-screen to the right. Bring it
       into view, centred where there is room.

       Sets scrollLeft on the row itself rather than calling
       scrollIntoView(), which would also scroll the page vertically to the
       nav and yank the shopper away from the product grid. Instant, not
       smooth: this is the initial state of the page, not a transition, so
       there is nothing to animate and nothing for prefers-reduced-motion
       to suppress. */
    revealCurrent() {
      const current = this.list.querySelector('[aria-current="page"], .is-active');
      if (!current) return;

      const listBox = this.list.getBoundingClientRect();
      const box = current.getBoundingClientRect();
      const fullyVisible = box.left >= listBox.left && box.right <= listBox.right;
      if (fullyVisible) return;

      const offsetInRow = this.list.scrollLeft + (box.left - listBox.left);
      const centred = offsetInRow - (this.list.clientWidth - box.width) / 2;
      const max = this.list.scrollWidth - this.list.clientWidth;

      this.list.scrollLeft = Math.max(0, Math.min(centred, max));
      this.update();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
      window.removeEventListener('resize', this.update);
    }

    /* A page is "most of the visible row" rather than its exact width, so the
       item that was at the edge stays partly in view and the jump reads as
       continuous instead of teleporting. */
    scrollByPage(direction) {
      const amount = Math.max(this.list.clientWidth * 0.8, 160);
      this.list.scrollBy({ left: amount * direction, behavior: 'smooth' });
    }

    update() {
      // 2px of slack: sub-pixel layout regularly leaves scrollLeft a fraction
      // short of its true maximum, which would otherwise leave the "next"
      // arrow showing forever at the end of the row.
      const slack = 2;
      const max = this.list.scrollWidth - this.list.clientWidth;
      const overflows = max > slack;

      this.classList.toggle('is-overflowing', overflows);
      this.prev.hidden = !overflows || this.list.scrollLeft <= slack;
      this.next.hidden = !overflows || this.list.scrollLeft >= max - slack;

      /* The stylesheet widens the row's edge fade on whichever side is
         currently carrying an arrow, so the arrow sits on content that is
         already fading out instead of chopping a legible label in half.
         It cannot read `hidden` on a child, hence these two classes. */
      this.classList.toggle('has-prev', !this.prev.hidden);
      this.classList.toggle('has-next', !this.next.hidden);
    }
  }

  customElements.define('collection-facet-nav-scroller', CollectionFacetNavScroller);
}
